import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Badge, Table } from 'react-bootstrap';
import { genericGetOne } from '../generic/crud';
import { ENTITY_ID_USER } from '../generic/Constants';

interface UserProfileDialogProps {
  open: boolean;
  onClose: () => void;
  user: any | null;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose, user }) => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user?.userId) {
      setLoading(true);
      setUserDetails(null);
      setError(null);
      genericGetOne(ENTITY_ID_USER, user.userId)
        .then(({ data }) => {
          setUserDetails(data);
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to fetch user details');
          setLoading(false);
        });
    } else if (!open) {
      setUserDetails(null);
      setError(null);
      setLoading(false);
    }
  }, [open, user]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return '-'; }
  };

  return (
    <Modal show={open} onHide={onClose} centered dialogClassName="mw-500px">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold">User Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {loading && <div className="text-center py-5"><Spinner animation="border" size="sm" /> Loading...</div>}
        {error && <div className="text-danger text-center py-4">{error}</div>}
        {userDetails && (
          <>
            {/* Profile Header */}
            <div className="d-flex align-items-center mb-4 p-3 bg-light rounded">
              <div className="me-4">
                {userDetails.profileImage ? (
                  <img src={userDetails.profileImage} alt="Profile" className="rounded-circle border shadow-sm" style={{width: 80, height: 80, objectFit: 'cover'}} />
                ) : (
                  <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{width: 80, height: 80, fontSize: 36}}>
                    <i className="fa fa-user" />
                  </div>
                )}
              </div>
              <div className="flex-grow-1">
                <h4 className="mb-1 fw-bold">{userDetails.name || '-'}</h4>
                <div className="text-muted mb-1">
                  <i className="fa fa-id-badge me-1"></i> {userDetails.id || '-'}
                </div>
                <div>
                  {userDetails.active
                    ? <Badge bg="success" className="px-3 py-2">Active</Badge>
                    : <Badge bg="danger" className="px-3 py-2">Inactive</Badge>
                  }
                  {userDetails.orgAdmin && <Badge bg="warning" text="dark" className="ms-2 px-3 py-2">Org Admin</Badge>}
                </div>
              </div>
            </div>

            {/* Contact & Role Details */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Email</div>
                  <div className="fw-semibold">{userDetails.email || '-'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Phone</div>
                  <div className="fw-semibold">{userDetails.phone || '-'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Designation</div>
                  <div className="fw-semibold">{userDetails.designationName || '-'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Role</div>
                  <div className="fw-semibold">{userDetails.roleName || '-'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Last Login</div>
                  <div className="fw-semibold">{formatDate(userDetails.loginDate)}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Login Count</div>
                  <div className="fw-semibold">{userDetails.loginCount != null ? userDetails.loginCount : '-'}</div>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="mb-2">
              <h6 className="fw-bold mb-3">Permissions</h6>
              {(() => {
                const perms = (userDetails.permissions && userDetails.permissions.length > 0)
                  ? userDetails.permissions
                  : (user && Array.isArray(user.permissions) && user.permissions.length > 0 ? user.permissions : []);
                return perms.length > 0 ? (
                  <Table bordered size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Entity</th>
                        <th className="text-center">Read</th>
                        <th className="text-center">Create</th>
                        <th className="text-center">Update</th>
                        <th className="text-center">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perms.map((perm: any, idx: number) => (
                        <tr key={`${perm.id}-${perm.permission}-${idx}`}>
                          <td>{perm.permission || '-'}</td>
                          <td className="text-center">{perm.read ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                          <td className="text-center">{perm.create ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                          <td className="text-center">{perm.update ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                          <td className="text-center">{perm.delete ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-muted fst-italic">No permissions found.</div>
                );
              })()}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserProfileDialog;
